import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { DurchfuehrungsTag } from '@chancenwerk/_cms/products/entities/Product.js';
import type { ProductLKK } from '@chancenwerk/_cms/products/entities/ProductLKK.js';
import type { KPF } from '@chancenwerk/master-data-management/kpf/entities/KPF.js';
import type { Student } from '@chancenwerk/master-data-management/users/entities/Student.js';
import { html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';

/**
 * @customElement chancen-component-choose-lkk-days
 *
 * Shows boxes to choose the Lernkaskaden-Durchführungstage for a Student based on some KPF.
 *
 * **Possible attrubutes:**
 *
 * - `kpf: KPF` - The KPF to pull the 'durchfuehrungsTage' from
 * - `student: Student` - The student to mutate
 *
 * **possible events:**
 *
 * - `value-picked: ValueEvent<Student>` - Emitted when the Student was changed
 */
@customElement('chancen-component-choose-lkk-days')
export class ChancenComponentChooseLkkDays extends ChemistryLitElement {
  @property({ attribute: true, type: Boolean }) disabled = false;
  @property({ attribute: false }) kpf?: KPF;
  @property({ attribute: false }) student?: Student;

  private _emitStudentChanged() {
    const event = new CustomEvent('value-picked', {
      detail: {
        value: this.student,
      },
    });
    this.dispatchEvent(event);
  }

  private _onChoiceClick(deactivated: boolean, day: DurchfuehrungsTag) {
    if (deactivated) return;

    const index = this.student?.lernfoerderungDuerchfuehrungsTagChoosen?.findIndex(
      c => c.wochentag === day.wochentag && c.startZeit?.toFormat('HH:mm') === day.startZeit?.toFormat('HH:mm'),
    );

    if (index === undefined || index === -1) this.student?.lernfoerderungDuerchfuehrungsTagChoosen?.push(day);
    else this.student?.lernfoerderungDuerchfuehrungsTagChoosen?.splice(index, 1);

    this._emitStudentChanged();
    this.requestUpdate();
  }

  private _checkDeactivated(choosen: boolean, day: DurchfuehrungsTag): boolean {
    if (!choosen) {
      if (
        this.student?.intensiveCoursesChoosen?.some(
          d => d.startZeit?.toFormat('HH:mm') === day.startZeit?.toFormat('HH:mm') && d.wochentag === day.wochentag,
        )
      ) {
        return true;
      }
    }
    return false;
  }

  private _choiceItem(deactivated: boolean, choosen: boolean, day: DurchfuehrungsTag) {
    if (!day.wochentag) {
      return nothing;
    }

    return html`
      <cr-basic-card
        ${css({
          boxShadow: 'none',
          cursor: deactivated ? 'not-allowed' : 'pointer',
          background: deactivated
            ? this.colors.neutral.shade(300)
            : choosen
            ? this.colors.primary.shade(100)
            : this.colors.tone.error.shade(100),
        })}
        @click=${() => {
          if (!this.disabled) this._onChoiceClick(deactivated, day);
        }}
      >
        <cr-flex horizontal center crossaxis-center space="sm">
          <cr-icon
            ${css({
              color: deactivated
                ? this.colors.neutral.shade(800)
                : choosen
                ? this.colors.primary
                : this.colors.tone.error,
              fontSize: '1.8rem',
            })}
          >
            ${deactivated ? 'not_interested' : choosen ? 'done' : 'close'}
          </cr-icon>
          <cr-text> ${day.wochentag} ${day.startZeit?.toFormat('HH:mm')} - ${day.endZeit?.toFormat('HH:mm')} </cr-text>
        </cr-flex>
      </cr-basic-card>
    `;
  }

  override render() {
    return html`<cr-flex space="sm" horizontal crossaxis-center wrap>
      ${!(this.kpf?.produkt as ProductLKK)?.durchfuehrungsTage?.length
        ? html` <cr-text> Um Tage zu wählen, muss ein KPF mit hinterlegten Tagen ausgewählt sein. </cr-text> `
        : nothing}
      ${repeat(
        (this.kpf?.produkt as ProductLKK)?.durchfuehrungsTage ?? [],
        key => key,
        day => {
          const choosen = this.student?.lernfoerderungDuerchfuehrungsTagChoosen?.some(
            d => d.wochentag === day.wochentag && d.startZeit?.toFormat('HH:mm') === day.startZeit?.toFormat('HH:mm'),
          );
          return this._choiceItem(this._checkDeactivated(!!choosen, day), !!choosen, day);
        },
      )}
    </cr-flex>`;
  }
}
